import React, { FunctionComponent } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";

import { createHandoffCode } from "../../api-client/handoffCodes";
import { getDealerBasePath } from "../../navigation/getDealerBasePath";
import { useCreateQuoteContactLead } from "../../queries/useCreateQuoteContactLead";
import { useCartName } from "../../stores/hooks/useCartName";
import { cartSlice } from "../../stores/slices/cartSlice";
import { getPdfDownloadLink } from "../../util/getPdfDownloadLink";
import { useAppointmentStepState } from "../Cart/Page/AppointmentStep/AppointmentStepModal";
import { setPendingQuoteEmail } from "../Cart/Page/AppointmentStep/pendingQuoteEmail";
import { useOrderDetails } from "../Cart/useOrderDetails";
import { LeadFormButtons } from "./LeadFormButtons";
import {
  LeadFormData,
  LeadFormModal,
  LeadParams,
  cartActionDownload,
  cartActionEmail,
  cartActionPrint,
  cartActionSaveQuote,
  useLeadFormModalState,
} from "./LeadFormModal";

const getActionPath = ({
  action,
  isOrder,
  orderNumber,
}: {
  action: "download" | "print";
  isOrder: boolean;
  orderNumber: string;
}): string => {
  const path = `${getDealerBasePath()}/${
    isOrder ? "order-details" : "wishlist"
  }/${action}/${orderNumber}`;

  return path;
};

export const LeadFormCart: FunctionComponent = () => {
  const downloadState = useLeadFormModalState("#download");
  const emailState = useLeadFormModalState("#email");
  const printState = useLeadFormModalState("#print");
  const saveQuoteState = useLeadFormModalState("#save-quote");
  const { t } = useTranslation();

  const createQuoteContactLead = useCreateQuoteContactLead();
  const { data: cart, isOrder } = useOrderDetails();
  const cartName = useCartName(cart);
  const dispatch = useDispatch();
  const [, { show: showAppointmentStep }] = useAppointmentStepState();

  // Saves the quote without the customer email; the appointment step sends it when it closes (TW-1134).
  const saveQuoteSubmitAction = async (
    params: LeadParams,
  ): Promise<LeadFormData> => {
    const lead = await createQuoteContactLead({
      ...params,
      sendCustomerEmail: false,
    });
    if (lead.leadId && cart) {
      setPendingQuoteEmail({
        hasEmail: Boolean(params.customerEmail),
        leadId: lead.leadId,
        orderNumber: cart.orderNumber,
      });
    }
    return lead;
  };

  const saveQuoteRedirectAction = ({
    customerEmail,
    customerFirstName,
    customerLastName,
    customerMessage,
    customerPhone,
    customerVin,
  }: LeadFormData): void => {
    // The lead call just stored these on the quote; mirror them locally so the appointment step (and the X-Time
    // URL, which re-saves whatever customer details it is sent) starts from what the customer typed.
    if (cart) {
      dispatch(
        cartSlice.actions.next({
          ...cart,
          customerEmail,
          customerName: [customerFirstName, customerLastName]
            .filter(Boolean)
            .join(" "),
          customerNotes: customerMessage,
          customerPhone,
          customerVin,
        }),
      );
    }
    showAppointmentStep();
  };

  const downloadRedirectActon = (): void => {
    // The link carries a one-time code for the session, not the token (TW-1087).
    createHandoffCode("pdf")
      .then((code) => {
        window.location.assign(
          getPdfDownloadLink({
            code,
            filename: `${cartName}.pdf`,
            orientation: "portrait",
            path: getActionPath({
              action: "download",
              isOrder,
              orderNumber: cart?.orderNumber ?? "",
            }),
          }),
        );
      })
      .catch(() => undefined); // the fetcher has shown the error
  };

  const printRedirectActon = (): void => {
    window.open(
      getActionPath({
        action: "print",
        isOrder,
        orderNumber: cart?.orderNumber ?? "",
      }),
      cartName
    );
  };

  return (
    <>
      <LeadFormButtons />
      <LeadFormModal
        cart={cart}
        generatedByPrefix=""
        hook={emailState}
        redirectAction={null}
        submitAction={createQuoteContactLead}
        subtitle={t(`leadForm.${isOrder ? "order" : "quote"}.subtitle.email`)}
        title={t(`leadForm.${isOrder ? "order" : "quote"}.title.email`)}
        type={cartActionEmail}
      />
      <LeadFormModal
        subtitle={t(
          `leadForm.${isOrder ? "order" : "quote"}.subtitle.download`
        )}
        cart={cart}
        generatedByPrefix=""
        hook={downloadState}
        redirectAction={downloadRedirectActon}
        submitAction={createQuoteContactLead}
        title={t(`leadForm.${isOrder ? "order" : "quote"}.title.download`)}
        type={cartActionDownload}
      />
      <LeadFormModal
        cart={cart}
        generatedByPrefix=""
        hook={printState}
        redirectAction={printRedirectActon}
        submitAction={createQuoteContactLead}
        subtitle={t(`leadForm.${isOrder ? "order" : "quote"}.subtitle.print`)}
        title={t(`leadForm.${isOrder ? "order" : "quote"}.title.print`)}
        type={cartActionPrint}
      />
      <LeadFormModal
        cart={cart}
        generatedByPrefix=""
        hook={saveQuoteState}
        redirectAction={saveQuoteRedirectAction}
        submitAction={saveQuoteSubmitAction}
        subtitle={null}
        title={t(`leadForm.${isOrder ? "order" : "quote"}.title.save`)}
        type={cartActionSaveQuote}
      />
    </>
  );
};
